import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from '../components/Alert.jsx';
import AnimatedCounter from '../components/AnimatedCounter.jsx';
import Loader from '../components/Loader.jsx';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';

// The launcher dashboard: a featured-package hero + a tile rail. Clicking a
// tile previews it in the hero; the hero carries the package's real CTAs
// (Start Setup / Request Access). Owned packages always sort before locked
// ones, and the first owned package is the default feature.

export default function Dashboard() {
  const { user } = useAuth();
  const [owned, setOwned] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(null);
  const [featured, setFeatured] = useState(null); // slug, null = default
  // "Request a Game" acknowledgement — client-side only (no backend queue
  // for game suggestions yet). The tile swaps to a ✓ state until reload.
  const [suggestDone, setSuggestDone] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([api.myPackages(), api.packagesCatalog()])
      .then(([m, c]) => {
        if (!alive) return;
        setOwned(m?.packages ?? []);
        setCatalog(c?.packages ?? []);
      })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  const ghosts = useMemo(() => {
    if (!owned || !catalog) return [];
    const ownedSlugs = new Set(owned.map((p) => p.slug));
    return catalog.filter((c) => !ownedSlugs.has(c.slug));
  }, [owned, catalog]);

  const totals = useMemo(() => {
    const overlayCount = (owned ?? []).reduce((n, p) => n + p.overlays.length, 0);
    return {
      packages: owned?.length ?? 0,
      overlays: overlayCount,
      locked: ghosts.length,
    };
  }, [owned, ghosts]);

  // One tile model for both owned and locked packages.
  const tiles = useMemo(() => {
    const ownedTiles = (owned ?? []).map((p) => {
      const total = catalog?.find((c) => c.slug === p.slug)?.totalOverlays ?? p.overlays.length;
      return {
        slug: p.slug,
        name: p.name,
        description: p.description,
        ownedPkg: true,
        overlays: p.overlays,
        total,
        missing: Math.max(0, total - p.overlays.length),
        countLabel: `${p.overlays.length} overlay${p.overlays.length === 1 ? '' : 's'}`,
        sub: p.overlays.length >= total
          ? `all ${total} overlay${total === 1 ? '' : 's'}`
          : `${p.overlays.length} of ${total} overlays`,
      };
    });
    const ghostTiles = ghosts.map((p) => ({
      slug: p.slug,
      name: p.name,
      description: p.description,
      ownedPkg: false,
      overlays: [],
      total: p.totalOverlays,
      missing: 0,
      countLabel: `${p.totalOverlays} available`,
      sub: 'Locked',
    }));
    return [...ownedTiles, ...ghostTiles];
  }, [owned, catalog, ghosts]);

  const feat = useMemo(() => {
    if (!tiles.length) return null;
    return tiles.find((t) => t.slug === featured) ?? tiles[0];
  }, [tiles, featured]);

  const canRequest = !user?.designRequestsDisabled;

  if (error) {
    return (
      <div className="row justify-content-center mt-5">
        <div className="col-md-8">
          <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>
        </div>
      </div>
    );
  }

  if (owned == null || catalog == null) {
    return <Loader label="Loading packages…" />;
  }

  return (
    <div className="dbl-page">
      <div className="dbl-head dbl-rv" style={{ animationDelay: '60ms' }}>
        <div>
          <div className="dbl-eyebrow">Produce Tournament Broadcasts</div>
          <h1 className="dbl-title">
            Welcome, <span className="dbl-title__name">{user?.name}</span>
          </h1>
        </div>
        <div className="dbl-stats" role="group" aria-label="Account summary">
          <div className="dbl-stat dbl-stat--blue">
            <div className="dbl-stat__value"><AnimatedCounter to={totals.overlays} duration={1200} /></div>
            <div className="dbl-stat__label">Overlays</div>
          </div>
          <span className="dbl-sep" aria-hidden="true" />
          <div className="dbl-stat">
            <div className="dbl-stat__value"><AnimatedCounter to={totals.packages} duration={1200} /></div>
            <div className="dbl-stat__label">Packages</div>
          </div>
          <span className="dbl-sep" aria-hidden="true" />
          <div className="dbl-stat dbl-stat--pink">
            <div className="dbl-stat__value"><AnimatedCounter to={totals.locked} duration={1200} /></div>
            <div className="dbl-stat__label">Requestable</div>
          </div>
          {user?.role === 'admin' && (
            <>
              <span className="dbl-sep" aria-hidden="true" />
              <Link to="/admin" className="dbl-admin">
                <span className="dbl-admin__prompt" aria-hidden="true">&gt;</span>
                Admin Panel
                <span className="dbl-admin__cursor" aria-hidden="true" />
              </Link>
            </>
          )}
        </div>
      </div>

      {tiles.length === 0 ? (
        <div className="dashboard-empty">
          <h2>No packages yet</h2>
          <p>Ask the admin to assign you a package.</p>
        </div>
      ) : (
        <>
          <section
            className="dbl-hero dbl-rv"
            style={{ animationDelay: '140ms' }}
            aria-label="Featured package"
          >
            <div
              key={feat.slug}
              className={`dbl-hero__art${feat.ownedPkg ? '' : ' dbl-hero__art--locked'}`}
              style={{ backgroundImage: `url(/api/packages/${feat.slug}/art/card)` }}
            />
            <div className="dbl-hero__scrim" aria-hidden="true" />
            <div className="dbl-hero__fade-b" aria-hidden="true" />
            <div className="dbl-hero__fade-t" aria-hidden="true" />
            <div className="dbl-hero__content">
              <div className="dbl-hero__eyebrow">Featured package</div>
              <h2 key={`t-${feat.slug}`} className="dbl-hero__name">{feat.name}</h2>
              <div className="dbl-hero__meta">
                {feat.ownedPkg ? (
                  <span className="dbl-chip dbl-chip--owned">Owned</span>
                ) : (
                  <span className="dbl-chip dbl-chip--locked">Locked</span>
                )}
                <span className="dot-sep" aria-hidden="true">·</span>
                <span className="dbl-count">{feat.countLabel}</span>
              </div>
              {feat.description && <p className="dbl-hero__desc">{feat.description}</p>}
              {feat.overlays.length > 0 && (
                <div className="dbl-hero__tags">
                  {feat.overlays.map((o) => (
                    <span className="dbl-tag" key={o.slug}>{o.name}</span>
                  ))}
                </div>
              )}
              <div className="dbl-hero__ctas">
                {feat.ownedPkg ? (
                  <Link to={`/${feat.slug}`} className="dbl-cta">Start Setup →</Link>
                ) : (
                  canRequest && (
                    <Link to={`/access/${feat.slug}`} className="dbl-cta dbl-cta--pink">
                      Request Access →
                    </Link>
                  )
                )}
                {feat.ownedPkg && feat.missing > 0 && canRequest && (
                  <Link to={`/access/${feat.slug}`} className="dbl-more">
                    Request access for {feat.missing} more overlay{feat.missing === 1 ? '' : 's'}
                  </Link>
                )}
              </div>
            </div>
          </section>

          <div className="dbl-sect dbl-rv" style={{ animationDelay: '260ms' }}>
            <div className="dbl-sect__title">Select Package</div>
            <div className="dbl-sect__rule" aria-hidden="true" />
            <div className="dbl-sect__hint">Click a tile to preview</div>
          </div>

          <div className="dbl-grid">
            {tiles.map((t, i) => (
              <button
                key={t.slug}
                type="button"
                className={[
                  'dbl-tile',
                  'dbl-rv',
                  t.ownedPkg ? '' : 'dbl-tile--locked',
                  t.slug === feat.slug ? 'is-featured' : '',
                ].filter(Boolean).join(' ')}
                style={{ animationDelay: `${380 + i * 60}ms` }}
                onClick={() => setFeatured(t.slug)}
                aria-pressed={t.slug === feat.slug}
              >
                <span
                  className="dbl-tile__art"
                  style={{ backgroundImage: `url(/api/packages/${t.slug}/art/card)` }}
                >
                  <span className="dbl-tile__shade" aria-hidden="true" />
                  <span className="dbl-tile__bar" aria-hidden="true" />
                  <span className={`dbl-chip dbl-tile__status ${t.ownedPkg ? 'dbl-chip--owned' : 'dbl-chip--locked'}`}>
                    {t.ownedPkg ? 'Owned' : 'Locked'}
                  </span>
                  <span className="dbl-tile__count">{t.countLabel}</span>
                </span>
                <span className="dbl-tile__name">{t.name}</span>
                <span className="dbl-tile__sub">{t.sub}</span>
              </button>
            ))}

            {/* "Request a Game" — same anatomy as the package tiles: CTA
                inside the art box, supporting text below it. Chrome stays
                visible for everyone, but interaction hides when
                design-requests are disabled for this user. */}
            {suggestDone ? (
              <div className="dbl-request is-static dbl-rv" style={{ animationDelay: '560ms' }}>
                <span className="dbl-request__art">
                  <span className="dbl-request__check" aria-hidden="true">✓</span>
                  <span className="dbl-request__title">Request received</span>
                </span>
                <span className="dbl-request__sub">Thanks — we&apos;ll consider it for a future release.</span>
              </div>
            ) : canRequest ? (
              <button
                type="button"
                className="dbl-request dbl-rv"
                style={{ animationDelay: '560ms' }}
                onClick={() => setSuggestDone(true)}
              >
                <span className="dbl-request__art">
                  <span className="dbl-request__plus" aria-hidden="true">+</span>
                  <span className="dbl-request__title">Request a Game</span>
                </span>
                <span className="dbl-request__sub">Don&apos;t see your game? Suggest one for a future release.</span>
              </button>
            ) : (
              <div className="dbl-request is-static dbl-rv" style={{ animationDelay: '560ms' }}>
                <span className="dbl-request__art">
                  <span className="dbl-request__plus" aria-hidden="true">+</span>
                  <span className="dbl-request__title">Request a Game</span>
                </span>
                <span className="dbl-request__sub">Don&apos;t see your game? Suggest one for a future release.</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
